import { array } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';

import joinClasses from 'webapp/helpers/join-classes';
import BarLoaderComponent from 'webapp/components/bar-loader';
import { OrderProperty } from 'webapp/components/reports/editor-base';
import ReportsFragmentGridComponent from 'webapp/components/reports/fragment-grid/index';
import ReportsViewerDescriptionComponent from 'webapp/components/reports/viewer/description';
import ReportsViewerFragmentBlockComponent from 'webapp/components/reports/viewer/fragment/block';
import ReportsViewerFragmentBoardComponent from 'webapp/components/reports/viewer/fragment/board';
import ReportsViewerFragmentImageComponent from 'webapp/components/reports/viewer/fragment/image';
import ReportsViewerFragmentStoryTextComponent from 'webapp/components/reports/viewer/fragment/story-text';
import ReportsViewerFragmentTitleComponent from 'webapp/components/reports/viewer/fragment/title';
import ReportsViewerNavigationComponent from 'webapp/components/reports/viewer/navigation';
import ReportsViewerSlideOuterComponent from 'webapp/components/reports/viewer/slide-outer';
import ReportsViewerSlideWrapperComponent from 'webapp/components/reports/viewer/slide-wrapper';

import type { ReportSlide } from 'webapp/models-custom';
import type Report from 'webapp/models-custom/report';

interface Args {
	model?: Report | null;
	isLoaderShown?: boolean;
	selectedSlide?: ReportSlide | null;
	onBoardPreviewHashChange?: (boardId: string, previewHash?: string) => void;
	onSlideChange?: (index: number) => void;
}

export default class ReportsViewerSlidesComponent extends Component<Args> {
	@service('event-manager') declare eventManagerService: Service.EventManager;

	@tracked isDescriptionOpen!: boolean;
	@tracked isDescriptionExpanded!: boolean;

	orderProperty = OrderProperty.ORDER;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.setIsDescriptionOpen(true);
		this.setIsDescriptionExpanded(false);
	}

	get selectedSlide(): ReportSlide | null {
		return this.args.selectedSlide || this.args.model?.slides[0] || null;
	}

	get selectedSlideId() {
		return this.args.selectedSlide?.id;
	}

	get hasDescription() {
		return !!this.args.selectedSlide?.description.content;
	}

	setIsDescriptionOpen(newValue: boolean) {
		this.isDescriptionOpen = newValue;
		this.eventManagerService.trigger(AppEvent.REPORT_VIEWER_DESCRIPTION_OPEN_TOGGLED, {
			newValue,
		});
	}

	setIsDescriptionExpanded(newValue: boolean) {
		this.isDescriptionExpanded = newValue;
		this.eventManagerService.trigger(AppEvent.REPORT_VIEWER_DESCRIPTION_EXPANDED_TOGGLED, {
			newValue,
		});
	}

	@action
	openSlide(slide: ReportSlide): void {
		const index = this.args.model!.sortedSlides.indexOf(slide);
		this.args.onSlideChange?.(index);
		this.setIsDescriptionExpanded(false);
	}

	@action
	toggleOpenDescription(): void {
		this.setIsDescriptionOpen(!this.isDescriptionOpen);

		if (!this.isDescriptionOpen && this.isDescriptionExpanded) {
			// can't have at the same time expanded AND closed description
			this.toggleExpandDescription();
		}
	}

	@action
	toggleExpandDescription(): void {
		this.setIsDescriptionExpanded(!this.isDescriptionExpanded);
	}
	<template>
		<div
			id="report-viewer_body"
			class="report_ct report_ct--slides {{unless this.selectedSlide 'report_ct--empty'}}"
		>

			{{#if this.selectedSlide}}

				<ReportsViewerNavigationComponent
					@model={{@model}}
					@selectedSlide={{this.selectedSlide}}
					@onOpenSlide={{this.openSlide}}
				/>

				<div
					class={{joinClasses
						(array
							"report_slide-and-desc_ct"
							(if this.hasDescription "has-desc")
							(if this.isDescriptionOpen "desc-open" "desc-closed")
							(if this.isDescriptionExpanded "desc-expanded" "desc-collapsed")
						)
					}}
					{{! have gradient in two places, hide/show correct with css depending on .present-view }}
					style={{this.selectedSlide.colorStyle}}
				>

					{{#if @isLoaderShown}}
						<div class="bar-container">
							<BarLoaderComponent @size="big" />
						</div>
					{{else if @model}}

						<ReportsViewerSlideOuterComponent @model={{this.selectedSlide}}>
							<ReportsViewerSlideWrapperComponent @model={{this.selectedSlide}}>
								<ReportsFragmentGridComponent
									@report={{@model}}
									@model={{this.selectedSlide.fragments}}
									@reportSlide={{this.selectedSlide}}
									@orderProperty={{this.orderProperty}}
									as |fragments Item|
								>
									{{#each fragments as |fragment|}}
										<Item @model={{fragment}}>
											{{#if fragment.isTypeBoard}}
												<ReportsViewerFragmentBoardComponent
													@model={{fragment}}
													@onBoardPreviewHashChange={{@onBoardPreviewHashChange}}
												/>
											{{else if fragment.isTypeImage}}
												<ReportsViewerFragmentImageComponent
													@model={{fragment}}
												/>
											{{else if fragment.isTypeBlock}}
												<ReportsViewerFragmentBlockComponent
													@model={{fragment}}
												/>
											{{else if fragment.isTypeStoryText}}
												<ReportsViewerFragmentStoryTextComponent
													@model={{fragment}}
												/>
											{{else if fragment.isTypeTitle}}
												<ReportsViewerFragmentTitleComponent
													@model={{fragment}}
												/>
											{{/if}}
										</Item>
									{{/each}}
								</ReportsFragmentGridComponent>
							</ReportsViewerSlideWrapperComponent>
						</ReportsViewerSlideOuterComponent>

						<ReportsViewerDescriptionComponent
							@model={{this.selectedSlide}}
							@isOpen={{this.isDescriptionOpen}}
							@isExpanded={{this.isDescriptionExpanded}}
							@toggleOpen={{this.toggleOpenDescription}}
							@toggleExpand={{this.toggleExpandDescription}}
						/>

					{{/if}}

				</div>

			{{else}}
				{{#if @isLoaderShown}}
					<div class="bar-container">
						<BarLoaderComponent @size="big" />
					</div>
				{{else}}
					This report has no slides.
				{{/if}}
			{{/if}}

		</div>
	</template>
}
